import { MutationTree } from 'vuex';
import {
    ApplicationFormState,
    ApplicationFormIndex,
    EditApplicationForm,
    UserRole,
    EventLogInfo,
    WorkLogInfo,
    PasswordInfo,
    NumberPerStatus,
    ActionName,
    EditAttachmentFile,
    DetectionRule,
    DetectionMailDestination,
} from './types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';
import Vue from 'vue';

// エラーをクリアする
const resetErrors = (state: ApplicationFormState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorTitle = undefined;
    searchState.errorDetails = undefined;

    const createState = state.create;
    createState.error = false;
    createState.errorTitle = undefined;
    createState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const filesState = state.files;
    filesState.error = false;
    filesState.errorCode = undefined;
    filesState.errorMsg = undefined;

    const detectionRulesState = state.detectionRules;
    detectionRulesState.error = false;
    detectionRulesState.errorCode = undefined;
    detectionRulesState.errorMsg = undefined;

    const mailDestinationsState = state.mailDestinations;
    mailDestinationsState.error = false;
    mailDestinationsState.errorCode = undefined;
    mailDestinationsState.errorMsg = undefined;

    // 申請書の申請に成功した場合にアップロードと申請書操作処理は消さないようにする
    if (!createState.isCreated) {
        const uploadFileState = state.uploadFiles;
        uploadFileState.error = false;
        uploadFileState.errorTitle = undefined;
        uploadFileState.errorDetails = undefined;

        const actionState = state.action;
        actionState.error = false;
        actionState.errorTitle = undefined;
        actionState.errorDetails = undefined;
    }

    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorTitle = undefined;
    csvState.errorDetails = undefined;

    const destroyFilesState = state.destroyFiles;
    destroyFilesState.error = false;
    destroyFilesState.errorTitle = undefined;
    destroyFilesState.errorDetails = undefined;

    const notificationState = state.notification;
    notificationState.error = false;
    notificationState.errorTitle = undefined;
    notificationState.errorDetails = undefined;

    const numberPerStatusState = state.numberPerStatus;
    numberPerStatusState.error = false;
    numberPerStatusState.errorTitle = undefined;
    numberPerStatusState.errorDetails = undefined;

    const usableActionsState = state.usableActions;
    usableActionsState.error = false;
    usableActionsState.errorTitle = undefined;
    usableActionsState.errorDetails = undefined;

    const attachmentFileDownloadState = state.attachmentFileDownload;
    attachmentFileDownloadState.error = false;
    attachmentFileDownloadState.errorTitle = undefined;
    attachmentFileDownloadState.errorDetails = undefined;
};

export const mutations: MutationTree<ApplicationFormState> = {
    // エラークリア
    [types.CLEAR_APPLICATION_FORM_ERROR](state) {
        const createState = state.create;

        // 申請書作成時(create)のみuploadFileStateとactionStateのデータは削除しない
        if (!createState.isCreated) {
            const uploadFilesState = state.uploadFiles;
            uploadFilesState.error = false;
            uploadFilesState.errorTitle = undefined;
            uploadFilesState.errorDetails = undefined;

            const actionState = state.action;
            actionState.error = false;
            actionState.errorTitle = undefined;
            actionState.errorDetails = undefined;
        }

        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isCreated = false;

        const updateState = state.update;
        updateState.error = false;
        updateState.errorTitle = undefined;
        updateState.errorDetails = undefined;
    },

    // 申請書一覧系
    [types.SEARCH_START](state) {
        resetErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; forms: Array<ApplicationFormIndex> }) {
        state.formCount = payload.count;
        state.forms = payload.forms;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const searchState = state.search;
        searchState.errorTitle = payload.errorTitle;
        searchState.errorDetails = payload.errorDetails;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // 申請作成系
    [types.CREATE_START](state) {
        resetErrors(state);
        const createState = state.create;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state, payload: { id: number; step_id: number }) {
        state.createdFormId = payload.id;
        state.createdFormStepId = payload.step_id;

        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // 申請書詳細情報参照系
    [types.SHOW_START](state) {
        resetErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](
        state,
        payload: {
            show: { application_form: EditApplicationForm; node_account_table_per_page: number };
            roles: Array<UserRole>;
            templateCheckForConfirmer: boolean;
        }
    ) {
        const showState = state.show;
        state.formDetails = payload.show.application_form;
        state.roles = payload.roles;
        state.node_account_table_per_page = payload.show.node_account_table_per_page;
        state.templateCheckForConfirmer = payload.templateCheckForConfirmer;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // 申請書詳細添付ファイル情報
    [types.FILES_START](state) {
        const filesState = state.files;
        filesState.isProcessing = true;
        filesState.isFetchSuccess = false;
    },

    [types.FILES_SUCCESS](state, payload: EditAttachmentFile) {
        Vue.set(state.formDetails, 'files', payload);

        const filesState = state.files;
        filesState.isProcessing = false;
        filesState.isFetchSuccess = true;
    },

    [types.FILES_FAILURE](state, payload: { code: string; message: string }) {
        const filesState = state.files;
        filesState.errorCode = payload.code;
        filesState.errorMsg = payload.message;
        filesState.error = true;
        filesState.isProcessing = false;
        filesState.isFetchSuccess = false;
    },

    // 申請書詳細検知ルール情報
    [types.DETECTION_RULES_START](state) {
        const detectionRulesState = state.detectionRules;
        detectionRulesState.isProcessing = true;
        detectionRulesState.isFetchSuccess = false;
    },

    [types.DETECTION_RULES_SUCCESS](state, payload: Array<DetectionRule>) {
        Vue.set(state.formDetails, 'detection_rules', payload);

        const detectionRulesState = state.detectionRules;
        detectionRulesState.isProcessing = false;
        detectionRulesState.isFetchSuccess = true;
    },

    [types.DETECTION_RULES_FAILURE](state, payload: { code: string; message: string }) {
        const detectionRulesState = state.detectionRules;
        detectionRulesState.errorCode = payload.code;
        detectionRulesState.errorMsg = payload.message;
        detectionRulesState.error = true;
        detectionRulesState.isProcessing = false;
        detectionRulesState.isFetchSuccess = false;
    },

    // 申請書詳細検知メール情報
    [types.MAIL_DESTINATIONS_START](state) {
        const mailDestinationsState = state.mailDestinations;
        mailDestinationsState.isProcessing = true;
        mailDestinationsState.isFetchSuccess = false;
    },

    [types.MAIL_DESTINATIONS_SUCCESS](state, payload: Array<DetectionMailDestination>) {
        Vue.set(state.formDetails, 'detection_mail_destinations', payload);

        const mailDestinationsState = state.mailDestinations;
        mailDestinationsState.isProcessing = false;
        mailDestinationsState.isFetchSuccess = true;
    },

    [types.MAIL_DESTINATIONS_FAILURE](state, payload: { code: string; message: string }) {
        const mailDestinationsState = state.mailDestinations;
        mailDestinationsState.errorCode = payload.code;
        mailDestinationsState.errorMsg = payload.message;
        mailDestinationsState.error = true;
        mailDestinationsState.isProcessing = false;
        mailDestinationsState.isFetchSuccess = false;
    },

    // 申請操作系
    [types.ACTION_START](state) {
        resetErrors(state);
        const actionState = state.action;
        actionState.isProcessing = true;
    },

    [types.ACTION_SUCCESS](state) {
        const actionState = state.action;
        actionState.isProcessing = false;
    },

    [types.ACTION_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const actionState = state.action;
        actionState.errorTitle = payload.errorTitle;
        actionState.errorDetails = payload.errorDetails;
        actionState.error = true;
        actionState.isProcessing = false;
    },

    // 申請書更新系
    [types.UPDATE_START](state) {
        resetErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state, payload: { step_id: number }) {
        state.createdFormStepId = payload.step_id;
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const csvState = state.csv;
        csvState.errorTitle = payload.errorTitle;
        csvState.errorDetails = payload.errorDetails;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // 申請書詳細関連情報イベントログ参照系
    [types.EVENT_LOG_START](state) {
        resetErrors(state);
        const eventLogState = state.eventLogs;
        eventLogState.isProcessing = true;
        eventLogState.isFetchSuccess = false;
    },

    [types.EVENT_LOG_SUCCESS](state, payload: { count: number; event_logs: Array<EventLogInfo> }) {
        state.eventLogCount = payload.count;
        state.eventLogList = payload.event_logs;

        const eventLogState = state.eventLogs;
        eventLogState.isProcessing = false;
        eventLogState.isFetchSuccess = true;
    },

    [types.EVENT_LOG_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const eventLogState = state.eventLogs;
        eventLogState.errorTitle = payload.errorTitle;
        eventLogState.errorDetails = payload.errorDetails;
        eventLogState.error = true;
        eventLogState.isProcessing = false;
        eventLogState.isFetchSuccess = false;
    },

    // 申請書詳細関連情報操作記録参照系
    [types.WORK_LOG_START](state) {
        resetErrors(state);
        const workLogState = state.workLogs;
        workLogState.isProcessing = true;
    },

    [types.WORK_LOG_SUCCESS](state, payload: WorkLogInfo) {
        const workLogState = state.workLogs;
        state.workLogInfo = payload;
        workLogState.isProcessing = false;
    },

    [types.WORK_LOG_FAILURE](state, payload: { code: string; message: string }) {
        const workLogState = state.workLogs;
        workLogState.errorCode = payload.code;
        workLogState.errorMsg = payload.message;
        workLogState.error = true;
        workLogState.isProcessing = false;
    },

    // 申請書詳細添付ファイルアップロード系（複数）
    [types.UPLOAD_FILES_START](state) {
        resetErrors(state);
        const uploadFileState = state.uploadFiles;
        uploadFileState.isProcessing = true;
    },

    [types.UPLOAD_FILES_SUCCESS](state) {
        const uploadFileState = state.uploadFiles;
        uploadFileState.isProcessing = false;
    },

    [types.UPLOAD_FILES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const uploadFileState = state.uploadFiles;
        uploadFileState.errorTitle = payload.errorTitle;
        uploadFileState.errorDetails = payload.errorDetails;
        uploadFileState.error = true;
        uploadFileState.isProcessing = false;
    },

    // 申請書詳細添付ファイルアップロード系
    [types.UPLOAD_FILE_START](state) {
        resetErrors(state);
        const uploadFileState = state.uploadFile;
        uploadFileState.isProcessing = true;
    },

    [types.UPLOAD_FILE_SUCCESS](state, payload: { id: number }) {
        state.createdFileId = payload.id;
        const uploadFileState = state.uploadFile;
        uploadFileState.isProcessing = false;
    },

    [types.UPLOAD_FILE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const uploadFileState = state.uploadFile;
        uploadFileState.errorTitle = payload.errorTitle;
        uploadFileState.errorDetails = payload.errorDetails;
        uploadFileState.error = true;
        uploadFileState.isProcessing = false;
    },

    // 申請書詳細添付ファイル削除系
    [types.DESTROY_FILE_START](state) {
        resetErrors(state);
        const destroyFilesState = state.destroyFiles;
        destroyFilesState.isProcessing = true;
    },

    [types.DESTROY_FILE_SUCCESS](state) {
        const destroyFilesState = state.destroyFiles;
        destroyFilesState.isProcessing = false;
    },

    [types.DESTROY_FILE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyFilesState = state.destroyFiles;
        destroyFilesState.errorTitle = payload.errorTitle;
        destroyFilesState.errorDetails = payload.errorDetails;
        destroyFilesState.error = true;
        destroyFilesState.isProcessing = false;
    },

    // 作業アカウントパスワード払出系
    [types.NOTIFICATION_START](state) {
        resetErrors(state);
        const notificationState = state.notification;
        notificationState.isProcessing = true;
    },

    [types.NOTIFICATION_SUCCESS](state, payload: PasswordInfo) {
        const notificationState = state.notification;
        state.passwordInfo = payload;
        notificationState.isProcessing = false;
    },

    [types.NOTIFICATION_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const notificationState = state.notification;
        notificationState.errorTitle = payload.errorTitle;
        notificationState.errorDetails = payload.errorDetails;
        notificationState.error = true;
        notificationState.isProcessing = false;
    },

    // ステータス毎の申請書件数の取得
    [types.NUMBER_PER_STATUS_START](state) {
        resetErrors(state);
        const numberPerStatusState = state.numberPerStatus;
        numberPerStatusState.isProcessing = true;
    },

    [types.NUMBER_PER_STATUS_SUCCESS](state, payload: NumberPerStatus) {
        const numberPerStatusState = state.numberPerStatus;
        state.numberPerStatusInfo = payload;
        numberPerStatusState.isProcessing = false;
    },

    [types.NUMBER_PER_STATUS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const numberPerStatusState = state.numberPerStatus;
        numberPerStatusState.errorTitle = payload.errorTitle;
        numberPerStatusState.errorDetails = payload.errorDetails;
        numberPerStatusState.error = true;
        numberPerStatusState.isProcessing = false;
    },

    // 申請書に対する可能操作の取得
    [types.USABLE_ACTIONS_START](state) {
        resetErrors(state);
        const usableActionsState = state.usableActions;
        usableActionsState.isProcessing = true;
    },

    [types.USABLE_ACTIONS_SUCCESS](state, payload: Array<ActionName>) {
        const usableActionsState = state.usableActions;
        state.usableActionsInfo = payload;
        usableActionsState.isProcessing = false;
    },

    [types.USABLE_ACTIONS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const usableActionsState = state.usableActions;
        usableActionsState.errorTitle = payload.errorTitle;
        usableActionsState.errorDetails = payload.errorDetails;
        usableActionsState.error = true;
        usableActionsState.isProcessing = false;
    },

    // 添付ファイルダウンロード系
    [types.ATTACHMENT_FILE_DOWNLOAD_START](state) {
        resetErrors(state);
        const attachmentFileDownloadState = state.attachmentFileDownload;
        attachmentFileDownloadState.isProcessing = true;
    },

    [types.ATTACHMENT_FILE_DOWNLOAD_SUCCESS](state) {
        const attachmentFileDownloadState = state.attachmentFileDownload;
        attachmentFileDownloadState.isProcessing = false;
    },

    [types.ATTACHMENT_FILE_DOWNLOAD_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const attachmentFileDownloadState = state.attachmentFileDownload;
        attachmentFileDownloadState.errorTitle = payload.errorTitle;
        attachmentFileDownloadState.errorDetails = payload.errorDetails;
        attachmentFileDownloadState.error = true;
        attachmentFileDownloadState.isProcessing = false;
    },

    // 状態クリア系
    [types.CLEAR_CREATED_FORM_ID](state) {
        state.createdFormId = undefined;
        state.createdFormStepId = undefined;
    },

    [types.CLEAR_CREATED_AFTER_SUCCESS](state) {
        state.create.isCreated = false;
    },

    [types.CLEAR_ALL](state) {
        resetErrors(state);
        state.forms = undefined;
        state.formDetails = undefined;
        state.formCount = 0;
        state.roles = [];
        state.eventLogList = undefined;
        state.eventLogCount = 0;
        state.workLogInfo = undefined;
        state.passwordInfo = undefined;
        state.numberPerStatusInfo = undefined;
        state.usableActionsInfo = undefined;
        state.templateCheckForConfirmer = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const filesState = state.files;
        filesState.isProcessing = false;
        filesState.isFetchSuccess = false;

        const detectionRulesState = state.detectionRules;
        detectionRulesState.isProcessing = false;
        detectionRulesState.isFetchSuccess = false;

        const mailDestinationsState = state.mailDestinations;
        mailDestinationsState.isProcessing = false;
        mailDestinationsState.isFetchSuccess = false;

        const actionState = state.action;
        actionState.isProcessing = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;

        const uploadFileState = state.uploadFiles;
        uploadFileState.isProcessing = false;

        const destroyFilesState = state.destroyFiles;
        destroyFilesState.isProcessing = false;

        const notificationState = state.notification;
        notificationState.isProcessing = false;

        const eventLogsState = state.eventLogs;
        eventLogsState.isProcessing = false;

        const workLogsState = state.workLogs;
        workLogsState.isProcessing = false;

        const numberPerStatusState = state.numberPerStatus;
        numberPerStatusState.isProcessing = false;

        const usableActionsState = state.usableActions;
        usableActionsState.isProcessing = false;

        const attachmentFileDownloadState = state.attachmentFileDownload;
        attachmentFileDownloadState.isProcessing = false;
    },
};
